import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  CheckCircleIcon,
  SparklesIcon,
  TrophyIcon,
  BoltIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const PlanCard = ({ plan, isPopular = false, onSelectPlan, className = '' }) => {
  const { t } = useTranslation();

  const planFeatures = {
    free: [
      { icon: CurrencyDollarIcon, text: t('subscription.features18Commission'), included: true, isNegative: true },
      { icon: BoltIcon, text: t('subscription.featuresGatewayFees'), included: true, isNegative: true },
      { icon: ShieldCheckIcon, text: t('subscription.featuresBasicSupport'), included: true },
      { icon: SparklesIcon, text: t('subscription.featuresCommissionExemption'), included: false },
      { icon: TrophyIcon, text: t('subscription.featuresPremiumSupport'), included: false },
      { icon: BoltIcon, text: t('subscription.features10FreeExchanges'), included: false }
    ],
    badeel_plus: [
      { icon: SparklesIcon, text: t('subscription.featuresFullExemption'), included: true, isHighlight: true },
      { icon: CurrencyDollarIcon, text: t('subscription.featuresGatewayFeesOnly'), included: true },
      { icon: BoltIcon, text: t('subscription.features10FreeExchanges'), included: true, isHighlight: true },
      { icon: TrophyIcon, text: t('subscription.featuresPremiumSupportPriority'), included: true }
    ]
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        duration: 0.6, 
        ease: [0.25, 0.46, 0.45, 0.94] 
      }
    },
    hover: { 
      y: -10, 
      scale: 1.02,
      boxShadow: "0 25px 50px -12px rgba(7, 119, 194, 0.25)",
      transition: { duration: 0.3 }
    }
  };

  const isPremium = plan.type === 'badeel_plus';

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      className={`relative group ${className}`}
    >
      {/* Popular Badge */}
      {isPopular && (
        <motion.div
          initial={{ scale: 0, rotate: -45 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
          className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10"
        >
          <div className="bg-gradient-to-r from-brand-blue to-brand-green text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
            <SparklesIcon className="w-4 h-4 inline-block mr-1" />
            {t('subscription.mostPopular')}
          </div>
        </motion.div>
      )}

      {/* Glow Effect for Premium */}
      {isPremium && (
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/20 via-brand-green/20 to-brand-blue/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      )}

      <div className={`relative h-full bg-white/95 backdrop-blur-lg rounded-3xl p-8 shadow-xl border-2 ${
        isPremium 
          ? 'border-gradient-to-r from-brand-blue to-brand-green' 
          : 'border-gray-200'
        } ${isPopular ? 'mt-4' : ''}`}>
        
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            animate={isPremium ? { rotate: [0, 5, -5, 0] } : {}}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            className={`w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
              isPremium 
                ? 'bg-gradient-to-br from-brand-blue to-brand-green' 
                : 'bg-gradient-to-br from-gray-400 to-gray-600'
            }`}
          >
            {isPremium ? (
              <TrophyIcon className="w-10 h-10 text-white" />
            ) : (
              <ShieldCheckIcon className="w-10 h-10 text-white" />
            )}
          </motion.div>

          <h3 className={`text-2xl font-bold mb-2 ${
            isPremium ? 'bg-gradient-to-r from-brand-blue to-brand-green bg-clip-text text-transparent' : 'text-gray-800'
          }`}>
            {plan.name}
          </h3>

          <p className="text-gray-600 mb-6">{plan.description}</p>

          {/* Price */}
          <div className="mb-6">
            {isPremium ? (
              <div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-4xl font-black text-brand-blue">2,400</span>
                  <span className="text-lg text-gray-600">ريال</span>
                </div>
                <div className="text-sm text-gray-500 mb-1">+ 360 {t('subscription.yearlyPrice')} {t('subscription.vat')}</div>
                <div className="text-xl font-bold text-brand-green">= 2,760 {t('subscription.yearlyPrice')}</div>
                <div className="text-sm text-gray-500 mt-2">{t('subscription.monthlyEquivalent')}</div>
              </div>
            ) : (
              <div>
                <span className="text-4xl font-black text-gray-800">{t('subscription.free')}</span>
                <div className="text-sm text-gray-500 mt-2">{t('subscription.with18Commission')}</div>
              </div>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="space-y-4 mb-8">
          {planFeatures[plan.type]?.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * index, duration: 0.4 }}
              className="flex items-center gap-3"
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                feature.included
                  ? feature.isHighlight
                    ? 'bg-gradient-to-r from-brand-blue to-brand-green'
                    : feature.isNegative
                      ? 'bg-orange-100'
                      : 'bg-green-100'
                  : 'bg-gray-100'
              }`}>
                {feature.included ? (
                  <CheckCircleIcon className={`w-4 h-4 ${
                    feature.isHighlight
                      ? 'text-white'
                      : feature.isNegative
                        ? 'text-orange-600'
                        : 'text-green-600'
                  }`} />
                ) : (
                  <div className="w-2 h-2 bg-gray-400 rounded-full" />
                )}
              </div>
              <span className={`text-sm ${
                feature.included
                  ? feature.isHighlight
                    ? 'text-brand-blue font-semibold'
                    : feature.isNegative
                      ? 'text-orange-700'
                      : 'text-gray-700'
                  : 'text-gray-400'
              }`}>
                {feature.text}
              </span>
            </motion.div>
          ))}
        </div>

        {/* CTA Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onSelectPlan?.(plan)}
          className={`w-full py-4 px-6 rounded-2xl font-bold text-lg transition-all duration-300 ${
            isPremium
              ? 'bg-gradient-to-r from-brand-blue via-brand-blueSecondary to-brand-blue text-white shadow-lg hover:shadow-xl'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          disabled={!isPremium}
        >
          {isPremium ? (
            <span className="flex items-center justify-center gap-2">
              <SparklesIcon className="w-5 h-5" />
              {t('subscription.subscribeNowBtn')}
            </span>
          ) : (
            t('subscription.currentPlanBtn')
          )}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default PlanCard;
